import { enqueueStableLoadBricks, flushStableLoadBricks, loadBricksImperatively, loadProcessorsImperatively, loadScript, loadStyle } from "@next-core/loader";
import { isTrackAll } from "@next-core/cook";
import { hasOwnProperty } from "@next-core/utils/general";
import { strictCollectMemberUsage } from "@next-core/utils/storyboard";
import { debounce, isEqual, omitBy } from "lodash";
import { asyncCheckBrickIf } from "./compute/checkIf.js";
import { asyncComputeRealPropertyEntries, constructAsyncProperties } from "./compute/computeRealProperties.js";
import { clearResolveCache, resolveData } from "./data/resolveData.js";
import { asyncComputeRealValue } from "./compute/computeRealValue.js";
import { listenOnTrackingContext, trackAfterInitial } from "./compute/listenOnTrackingContext.js";
import { matchRoute, matchRoutes } from "./matchRoutes.js";
import { symbolForAsyncComputedPropsFromHost, symbolForTPlExternalForEachIndex, symbolForTPlExternalForEachItem, symbolForTPlExternalForEachSize, symbolForTplStateStoreId } from "./CustomTemplates/constants.js";
import { expandCustomTemplate } from "./CustomTemplates/expandCustomTemplate.js";
import { getTagNameOfCustomTemplate, getTplStateStore } from "./CustomTemplates/utils.js";
import { customTemplates } from "../CustomTemplates.js";
import { getBrickPackages, hooks } from "./Runtime.js";
import { RenderTag } from "./enums.js";
import { getTracks } from "./compute/getTracks.js";
import { isStrictMode, warnAboutStrictMode } from "../isStrictMode.js";
import { FORM_RENDERER, symbolForFormStateStoreId } from "./FormRenderer/constants.js";
import { expandFormRenderer } from "./FormRenderer/expandFormRenderer.js";
import { registerFormRenderer } from "./FormRenderer/registerFormRenderer.js";
import { isPreEvaluated } from "./compute/evaluate.js";
import { getPreEvaluatedRaw } from "./compute/evaluate.js";
import { matchHomepage } from "./matchStoryboard.js";
import { listenerFactory } from "./bindListeners.js";
import { setupRootRuntimeContext } from "./setupRootRuntimeContext.js";
import { setMatchedRoute } from "./routeMatchedMap.js";
import { ErrorNode } from "./ErrorNode.js";
export async function renderRoutes(returnNode, routes, _runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, isIncremental, initialTracker) {
  const matched = await matchRoutes(routes, _runtimeContext);
  const output = getEmptyRenderOutput();
  const menuRequestNode = output.menuRequestNode = {
    return: menuRequestReturnNode
  };
  switch (matched) {
    case "missed":
      break;
    case "unauthenticated":
      output.unauthenticated = true;
      break;
    default:
      {
        var _hooks$checkPermissio;
        const route = output.route = matched.route;
        output.path = matched.match.path;
        const runtimeContext = {
          ..._runtimeContext,
          match: matched.match
        };
        if (route.iid) {
          setMatchedRoute(route.iid, matched.match);
        }
        if (isIncremental) {
          runtimeContext.ctxStore.disposeDataInRoutes(routes);
        }
        const routePath = parentRoutes.concat({
          route,
          routes
        });
        runtimeContext.ctxStore.define(route.context, runtimeContext, undefined, routePath);
        runtimeContext.pendingPermissionsPreCheck.push(hooks === null || hooks === void 0 || (_hooks$checkPermissio = hooks.checkPermissions) === null || _hooks$checkPermissio === void 0 ? void 0 : _hooks$checkPermissio.preCheckPermissionsForBrickOrRoute(route, value => asyncComputeRealValue(value, runtimeContext)));

        // Currently, this is only used for brick size-checking: these bricks
        // will be loaded before page rendering, but they will NOT be rendered.
        const {
          preLoadBricks
        } = route;
        if (Array.isArray(preLoadBricks)) {
          output.blockingList.push(loadBricksImperatively(preLoadBricks, getBrickPackages()));
        }
        if (route.type === "redirect") {
          let redirectTo;
          if (typeof route.redirect === "string") {
            redirectTo = await asyncComputeRealValue(route.redirect, runtimeContext);
          } else {
            const resolved = await resolveData({
              transform: "redirect",
              ...route.redirect
            }, runtimeContext);
            redirectTo = resolved.redirect;
          }
          if (typeof redirectTo !== "string") {
            // eslint-disable-next-line no-console
            console.error("Unexpected redirect result:", redirectTo);
            throw new Error(`Unexpected type of redirect result: ${typeof redirectTo}`);
          }
          output.redirect = {
            path: redirectTo
          };
        } else {
          const menuRequest = loadMenu(route.menu, runtimeContext);
          if (menuRequest) {
            menuRequestNode.request = menuRequest;
          }
          if (!isIncremental) {
            rendererContext.memoizeMenuRequestNode(routes, menuRequestNode);
          }
          const usedProcessors = strictCollectMemberUsage(route.context, "PROCESSORS", 2);
          if (usedProcessors.size > 0) {
            output.blockingList.push(catchLoad(loadProcessorsImperatively(usedProcessors, getBrickPackages()), "processors", [...usedProcessors].join(", "), rendererContext.unknownBricks));
          }
          let newOutput;
          if (route.type === "routes") {
            newOutput = await renderRoutes(returnNode, route.routes, runtimeContext, rendererContext, routePath, menuRequestNode, slotId, undefined, initialTracker);
          } else {
            newOutput = await renderBricks(returnNode, route.bricks, runtimeContext, rendererContext, routePath, menuRequestNode, slotId, undefined, initialTracker);
          }
          mergeRenderOutput(output, newOutput);
          appendMenuRequestNode(menuRequestNode, newOutput.menuRequestNode);
        }
      }
  }
  return output;
}
export async function renderBricks(returnNode, bricks, runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack, initialTracker) {
  setupRootRuntimeContext(bricks, runtimeContext, true);
  const output = getEmptyRenderOutput();
  // 多个构件并行异步转换，但转换的结果按原顺序串行合并。
  const rendered = await Promise.all(bricks.map(brickConf => renderBrick(returnNode, brickConf, runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack && new Map(tplStack), initialTracker)));
  rendered.forEach(item => {
    mergeRenderOutput(output, item);
  });
  return output;
}
export async function renderBrick(returnNode, brickConf, _runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId) {
  let tplStack = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : new Map();
  let initialTracker = arguments.length > 8 ? arguments[8] : undefined;
  try {
    return await legacyRenderBrick(returnNode, brickConf, _runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack, initialTracker);
  } catch (error) {
    if (brickConf.errorBoundary) {
      // eslint-disable-next-line no-console
      console.error("Error caught by error boundary:", error);
      return {
        node: await ErrorNode(error, returnNode),
        blockingList: []
      };
    } else {
      throw error;
    }
  }
}
async function legacyRenderBrick(returnNode, brickConf, _runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack, initialTracker) {
  var _hooks$checkPermissio2;
  const output = getEmptyRenderOutput();
  if (!brickConf.brick) {
    if (brickConf.template) {
      // eslint-disable-next-line no-console
      console.error("Legacy templates are dropped in v3:", brickConf);
    } else {
      // eslint-disable-next-line no-console
      console.error("Invalid brick:", brickConf);
    }
    return output;
  }

  // Translate `if: "<%= ... %>"` to `brick: ":if", dataSource: "<%= ... %>"`.
  // In other words, translate tracking if expressions to tracking control nodes of `:if`.
  const {
    if: brickIf,
    permissionsPreCheck,
    ...restBrickConf
  } = brickConf;
  if (isGeneralizedTrackAll(brickIf)) {
    return renderBrick(returnNode, {
      brick: ":if",
      iid: brickConf.iid,
      dataSource: brickIf,
      // `permissionsPreCheck` maybe required before computing `if`.
      permissionsPreCheck,
      slots: {
        "": {
          type: "bricks",
          bricks: [restBrickConf]
        }
      },
      // These symbols have to be copied to the new brick conf.
      ...Object.getOwnPropertySymbols(brickConf).reduce((acc, symbol) => ({
        ...acc,
        [symbol]: brickConf[symbol]
      }), {})
    }, _runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack, initialTracker);
  }
  const tplStateStoreId = brickConf[symbolForTplStateStoreId];
  const formStateStoreId = brickConf[symbolForFormStateStoreId];
  const runtimeContext = {
    ..._runtimeContext,
    tplStateStoreId,
    formStateStoreId
  };
  if (hasOwnProperty(brickConf, symbolForTPlExternalForEachItem)) {
    // The external bricks of a template should restore their `forEach*` from their host.
    runtimeContext.forEachItem = brickConf[symbolForTPlExternalForEachItem];
    runtimeContext.forEachIndex = brickConf[symbolForTPlExternalForEachIndex];
    runtimeContext.forEachSize = brickConf[symbolForTPlExternalForEachSize];
  }
  const strict = isStrictMode(runtimeContext);
  const {
    context
  } = brickConf;
  // istanbul ignore next
  if (Array.isArray(context) && context.length > 0) {
    warnAboutStrictMode(strict, "Defining context on bricks", "check your brick:", brickConf);
    if (!strict) {
      runtimeContext.ctxStore.define(context, runtimeContext);
    }
  }
  runtimeContext.pendingPermissionsPreCheck.push(hooks === null || hooks === void 0 || (_hooks$checkPermissio2 = hooks.checkPermissions) === null || _hooks$checkPermissio2 === void 0 ? void 0 : _hooks$checkPermissio2.preCheckPermissionsForBrickOrRoute(brickConf, value => asyncComputeRealValue(value, runtimeContext)));
  if (!(await asyncCheckBrickIf(brickConf, runtimeContext))) {
    return output;
  }
  const brickName = brickConf.brick;
  if (brickName.startsWith(":")) {
    var _brickConf$lifeCycle;
    ensureValidControlBrick(brickName);
    const {
      dataSource
    } = brickConf;
    const {
      contextNames,
      stateNames
    } = getTracks(dataSource);
    const tracking = !!(contextNames || stateNames);
    const lowerLevelRenderControlNode = async (runtimeContext, tracker, trackDataSource) => {
      var _slots$slot;
      // First, compute the `dataSource`
      const computedDataSource = await asyncComputeRealValue(dataSource, runtimeContext);
      if (trackDataSource) {
        trackAfterInitial(runtimeContext, [{
          contextNames,
          stateNames,
          propValue: dataSource
        }], tracker);
      }

      // Then, get the matched slot.
      const slot = brickName === ":forEach" ? "" : brickName === ":switch" ? String(computedDataSource) : computedDataSource ? "" : "else";

      // Don't forget to transpile children to slots.
      const slots = childrenToSlots(brickConf.children, brickConf.slots);

      // Then, get the bricks in that matched slot.
      const bricks = slots && hasOwnProperty(slots, slot) && ((_slots$slot = slots[slot]) === null || _slots$slot === void 0 ? void 0 : _slots$slot.bricks);
      const output = getEmptyRenderOutput();
      const abstractNode = {
        tag: RenderTag.ABSTRACT,
        return: returnNode,
        iid: brickConf.iid
      };
      output.node = abstractNode;
      if (!Array.isArray(bricks)) {
        return output;
      }
      let childrenOutput;
      switch (brickName) {
        case ":forEach":
          {
            if (!Array.isArray(computedDataSource)) {
              break;
            }
            childrenOutput = await renderForEach(abstractNode, computedDataSource, bricks, runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack, tracker);
            break;
          }
        case ":if":
        case ":switch":
          {
            childrenOutput = await renderBricks(abstractNode, bricks, runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack, tracker);
          }
      }
      if (childrenOutput) {
        abstractNode.child = childrenOutput.node;
        mergeRenderOutput(output, {
          ...childrenOutput,
          node: undefined
        });
      }
      return output;
    };
    const renderControlNode = async _ref => {
      let {
        type,
        runtimeContext,
        tplStateStoreScope,
        formStateStoreScope
      } = _ref;
      let changedAfterInitial = false;
      const tracker = {
        disposes: [],
        listener: () => {
          changedAfterInitial = true;
        }
      };
      let rerenderCount = 0;
      let rawOutput;
      let uninitialized = true;

      // When perform an incremental sub-route render, for control nodes,
      // context maybe changed just after their data source being computed,
      // as well as props of their children bricks being computed, and before
      // bricks being mounted. Thus these changes may not take any effects.
      // So we need to track the context changes after the initial render,
      // and perform re-renders for these control nodes if necessary.
      while (uninitialized || changedAfterInitial) {
        changedAfterInitial = false;
        rawOutput = await lowerLevelRenderControlNode(runtimeContext, tracker, uninitialized && type === "initial");

        // Changes may happen during `postAsyncRender`.
        if (!changedAfterInitial && type === "rerender") {
          const scopedStores = [...tplStateStoreScope, ...formStateStoreScope];
          await postAsyncRender(rawOutput, runtimeContext, [runtimeContext.ctxStore, ...scopedStores]);
        }
        tracker.disposes.forEach(dispose => dispose());
        tracker.disposes.length = 0;
        uninitialized = false;
        // istanbul ignore next
        if (++rerenderCount > 10) {
          throw new Error(`Maximum rerender stack overflowed (iid: ${brickConf.iid})`);
        }
      }
      return rawOutput;
    };
    let controlledOutput = await renderControlNode({
      type: "initial",
      runtimeContext
    });
    const {
      onMount,
      onUnmount
    } = (_brickConf$lifeCycle = brickConf.lifeCycle) !== null && _brickConf$lifeCycle !== void 0 ? _brickConf$lifeCycle : {};
    if (tracking) {
      var _runtimeBrick$dispose;
      let renderId = 0;
      const listener = async () => {
        // TODO(steve): start listeners when mounting, and handle changes between rendering and mounting.
        if (!returnNode.mounted || returnNode.disposed) {
          return;
        }
        const currentRenderId = ++renderId;
        const [scopedRuntimeContext, tplStateStoreScope, formStateStoreScope] = createScopedRuntimeContext(runtimeContext);
        const reControlledOutput = await renderControlNode({
          type: "rerender",
          runtimeContext: scopedRuntimeContext,
          tplStateStoreScope,
          formStateStoreScope
        });

        // Ignore stale renders
        if (renderId === currentRenderId && returnNode.mounted && !returnNode.disposed) {
          if (onUnmount) {
            listenerFactory(onUnmount, runtimeContext)(new CustomEvent("unmount", {
              detail: {
                rerender: true
              }
            }));
          }
          rendererContext.reRender(returnNode, reControlledOutput.node, controlledOutput.node);
          controlledOutput = reControlledOutput;
          if (onMount) {
            listenerFactory(onMount, scopedRuntimeContext)(new CustomEvent("mount", {
              detail: {
                rerender: true
              }
            }));
          }
          for (const store of [...tplStateStoreScope, ...formStateStoreScope]) {
            store.mountAsyncData();
          }
        }
      };
      // Enable leading invocation, to keep tracking orders.
      const debouncedListener = debounce(listener, 0, {
        leading: true,
        trailing: true
      });
      const runtimeBrick = returnNode.tag === RenderTag.ROOT ? null : returnNode;
      const disposes = runtimeBrick ? (_runtimeBrick$dispose = runtimeBrick.disposes) !== null && _runtimeBrick$dispose !== void 0 ? _runtimeBrick$dispose : runtimeBrick.disposes = [] : [];
      if (contextNames) {
        for (const contextName of contextNames) {
          disposes.push(runtimeContext.ctxStore.onChange(contextName, debouncedListener));
        }
      }
      if (stateNames) {
        for (const contextName of stateNames) {
          const tplStateStore = getTplStateStore(runtimeContext, "STATE", `: "${dataSource}"`);
          disposes.push(tplStateStore.onChange(contextName, debouncedListener));
        }
      }
    }
    if (onMount) {
      rendererContext.registerArbitraryLifeCycle("onMount", () => {
        listenerFactory(onMount, runtimeContext)(new CustomEvent("mount", {
          detail: {
            rerender: false
          }
        }));
      });
    }
    if (onUnmount) {
      rendererContext.registerArbitraryLifeCycle("onUnmount", () => {
        listenerFactory(onUnmount, runtimeContext)(new CustomEvent("unmount", {
          detail: {
            rerender: false
          }
        }));
      });
    }
    return controlledOutput;
  }

  // Widgets need to be defined before rendering.
  if (/\.tpl-/.test(brickName) && !customTemplates.get(brickName)) {
    await catchLoad(loadBricksImperatively([brickName], getBrickPackages()), "brick", brickName, rendererContext.unknownBricks);
  }
  const tplTagName = getTagNameOfCustomTemplate(brickName, runtimeContext);
  if (tplTagName) {
    var _tplStack$get;
    const tplCount = (_tplStack$get = tplStack.get(tplTagName)) !== null && _tplStack$get !== void 0 ? _tplStack$get : 0;
    if (tplCount >= 10) {
      throw new Error(`Maximum custom template stack overflowed: "${tplTagName}"`);
    }
    tplStack.set(tplTagName, tplCount + 1);
  } else if (brickName.includes("-") && !customElements.get(brickName)) {
    if (brickName === FORM_RENDERER) {
      registerFormRenderer();
    } else {
      output.blockingList.push(catchLoad(enqueueStableLoadBricks([brickName], getBrickPackages()), "brick", brickName, rendererContext.unknownBricks));
    }
  }
  let formData;
  let confProps;
  if (brickName === FORM_RENDERER) {
    var _brickConf$properties, _brickConf$properties2;
    ({
      formData,
      ...confProps
    } = (_brickConf$properties = brickConf.properties) !== null && _brickConf$properties !== void 0 ? _brickConf$properties : {});
    if ((_brickConf$properties2 = brickConf.properties) !== null && _brickConf$properties2 !== void 0 && _brickConf$properties2.compute) {
      formData = await asyncComputeRealValue(formData, runtimeContext);
    }
  } else {
    if (runtimeContext.inUseBrick) {
      // Keep v2 behavior for `useBrick`: treat `transform` as `properties`.
      const transform = brickConf.transform;
      if (transform) {
        warnAboutStrictMode(strict, "`useBrick.transform`", 'please use "properties" instead, check your useBrick:', brickConf);
        if (!strict) {
          confProps = {
            ...brickConf.properties,
            ...transform
          };
        }
      }
    }
    confProps !== null && confProps !== void 0 ? confProps : confProps = brickConf.properties;
  }
  const trackingContextList = [];
  const asyncPropertyEntries = asyncComputeRealPropertyEntries(confProps, runtimeContext, trackingContextList);
  const computedPropsFromHost = brickConf[symbolForAsyncComputedPropsFromHost];
  if (computedPropsFromHost) {
    asyncPropertyEntries.push(...computedPropsFromHost);
  }
  const isScript = brickName === "script";
  if (isScript || brickName === "link") {
    const props = await constructAsyncProperties(asyncPropertyEntries);
    if (isScript ? props.src : props.rel === "stylesheet" && props.href) {
      var _window$PUBLIC_ROOT;
      const prefix = (_window$PUBLIC_ROOT = window.PUBLIC_ROOT) !== null && _window$PUBLIC_ROOT !== void 0 ? _window$PUBLIC_ROOT : "";
      if (isScript) {
        const {
          src,
          ...attrs
        } = props;
        await catchLoad(loadScript(src, prefix, attrs), "script", src, "silent");
      } else {
        const {
          href,
          ...attrs
        } = props;
        await catchLoad(loadStyle(href, prefix, attrs), "stylesheet", href, "silent");
      }
      return output;
    }
  }
  const brick = {
    tag: RenderTag.BRICK,
    type: tplTagName || brickName,
    return: returnNode,
    slotId,
    events: brickConf.events,
    runtimeContext,
    portal: brickConf.portal,
    iid: brickConf.iid,
    ref: brickConf.ref
  };
  output.node = brick;

  // 在最终挂载前，先加载所有可能用到的 processors。
  const usedProcessors = strictCollectMemberUsage([brickConf.events, brickConf.lifeCycle], "PROCESSORS", 2);
  if (usedProcessors.size > 0) {
    output.blockingList.push(catchLoad(loadProcessorsImperatively(usedProcessors, getBrickPackages()), "processors", [...usedProcessors].join(", "), rendererContext.unknownBricks));
  }

  // 加载构件属性和加载子构件等任务，可以并行。
  const blockingList = [];

  // Note: properties here has already been initialized.
  trackAfterInitial(runtimeContext, trackingContextList, initialTracker);
  const loadProperties = async () => {
    brick.properties = await constructAsyncProperties(asyncPropertyEntries);
    listenOnTrackingContext(brick, trackingContextList);
  };
  blockingList.push(loadProperties());
  rendererContext.registerBrickLifeCycle(brick, brickConf.lifeCycle);
  let expandedBrickConf = brickConf;
  if (tplTagName) {
    expandedBrickConf = expandCustomTemplate(tplTagName, brickConf, brick, asyncPropertyEntries, rendererContext);
  } else if (brickName === FORM_RENDERER) {
    expandedBrickConf = expandFormRenderer(formData, brickConf, brick, asyncPropertyEntries, rendererContext);
  }
  if (expandedBrickConf.portal) {
    // A portal brick has no slotId.
    brick.slotId = undefined;
  }
  let childRuntimeContext;
  if (tplTagName) {
    // There is a boundary for `forEachItem` between template internals and externals.
    childRuntimeContext = {
      ...runtimeContext
    };
    delete childRuntimeContext.forEachItem;
    delete childRuntimeContext.forEachIndex;
    delete childRuntimeContext.forEachSize;
  } else {
    childRuntimeContext = runtimeContext;
  }
  const loadChildren = async () => {
    const slots = childrenToSlots(expandedBrickConf.children, expandedBrickConf.slots);
    if (!slots) {
      return;
    }
    const routeSlotFromIndexToSlotId = new Map();
    const rendered = await Promise.all(Object.entries(slots).map(async (_ref2, index) => {
      let [childSlotId, slotConf] = _ref2;
      if (slotConf.type !== "routes") {
        return renderBricks(brick, slotConf.bricks, childRuntimeContext, rendererContext, parentRoutes, menuRequestReturnNode, childSlotId, tplStack, initialTracker);
      }
      let lastOutput = getEmptyRenderOutput();
      const abstractNode = {
        tag: RenderTag.ABSTRACT,
        return: brick
      };
      lastOutput.node = abstractNode;
      const {
        route: parentRoute
      } = parentRoutes[parentRoutes.length - 1];
      if (parentRoute !== null && parentRoute !== void 0 && parentRoute.incrementalSubRoutes) {
        routeSlotFromIndexToSlotId.set(index, childSlotId);
        rendererContext.performIncrementalRender(slotConf, parentRoutes.map(_ref3 => {
          let {
            route
          } = _ref3;
          return route;
        }), async (location, prevLocation) => {
          const {
            homepage
          } = childRuntimeContext.app;
          const {
            pathname
          } = location;
          // Ignore if any one of homepage and parent routes not matched.
          if (!matchHomepage(homepage, pathname) || !parentRoutes.every(_ref4 => {
            let {
              route,
              routes
            } = _ref4;
            let prevMatch = null;
            let newMatch = null;
            return (prevMatch = matchTargetRoute(route, routes, homepage, prevLocation.pathname)) && (newMatch = matchTargetRoute(route, routes, homepage, pathname)) && (route !== parentRoute || isRouteParamsEqual(prevMatch.params, newMatch.params));
          })) {
            return false;
          }
          clearResolveCache();
          const [scopedRuntimeContext, tplStateStoreScope, formStateStoreScope] = createScopedRuntimeContext({
            ...childRuntimeContext,
            location,
            query: new URLSearchParams(location.search)
          });
          let failed = false;
          let incrementalOutput;
          let scopedStores = [];
          const newOutput = getEmptyRenderOutput();
          const newControlNode = {
            tag: RenderTag.ABSTRACT,
            return: brick
          };
          newOutput.node = newControlNode;
          try {
            incrementalOutput = await renderRoutes(newControlNode, slotConf.routes, scopedRuntimeContext, rendererContext, parentRoutes, menuRequestReturnNode, childSlotId, true);

            // Do not ignore incremental rendering even if all sub-routes are missed.
            // Since parent route is matched.
            if (incrementalOutput.route) {
              // Bailout if redirect or unauthenticated is set
              if (rendererContext.reBailout(incrementalOutput)) {
                return true;
              }
              scopedStores = [...tplStateStoreScope, ...formStateStoreScope];
              await postAsyncRender(incrementalOutput, scopedRuntimeContext, [scopedRuntimeContext.ctxStore, ...scopedStores]);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Incremental sub-router failed:", error);
            const result = await rendererContext.reCatch(error, newControlNode);
            if (!result) {
              return true;
            }
            ({
              failed,
              output: incrementalOutput
            } = result);
          }

          // istanbul ignore next: covered by e2e tests
          if (brick.disposed) {
            return true;
          }
          newControlNode.child = incrementalOutput.node;
          mergeRenderOutput(newOutput, {
            ...incrementalOutput,
            node: undefined
          });
          // Assert: no errors will be throw
          await rendererContext.reMergeMenuRequestNodes(menuRequestReturnNode, slotConf.routes, incrementalOutput.menuRequestNode);
          rendererContext.reRender(brick, newControlNode, lastOutput.node);
          lastOutput = newOutput;
          if (!failed) {
            scopedRuntimeContext.ctxStore.mountAsyncData(incrementalOutput.route);
            for (const store of scopedStores) {
              store.mountAsyncData();
            }
          }

          // Even if all sub-routes missed, treat the incremental rendering as performed.
          return true;
        });
      }
      const routesOutput = await renderRoutes(abstractNode, slotConf.routes, childRuntimeContext, rendererContext, parentRoutes, menuRequestReturnNode, childSlotId, undefined, initialTracker);
      abstractNode.child = routesOutput.node;
      mergeRenderOutput(output, {
        ...routesOutput,
        node: undefined
      });
      appendMenuRequestNode(menuRequestReturnNode, output.menuRequestNode = routesOutput.menuRequestNode);
      return lastOutput;
    }));
    const childrenOutput = {
      ...output,
      node: undefined,
      blockingList: [],
      menuRequestNode: undefined
    };
    rendered.forEach(item => {
      mergeRenderOutput(childrenOutput, item);
      mergeSiblingRenderMenuRequest(childrenOutput, item);
    });
    if (childrenOutput.node) {
      brick.child = childrenOutput.node;
    }
    mergeRenderOutput(output, {
      ...childrenOutput,
      node: undefined
    });
    appendMenuRequestNode(menuRequestReturnNode, output.menuRequestNode = childrenOutput.menuRequestNode);
  };
  blockingList.push(loadChildren());
  await Promise.all(blockingList);
  return output;
}
function isGeneralizedTrackAll(brickIf) {
  return typeof brickIf === "string" ? isTrackAll(brickIf) : isPreEvaluated(brickIf) &&
  // istanbul ignore next: covered by e2e tests
  isTrackAll(getPreEvaluatedRaw(brickIf));
}
function ensureValidControlBrick(brick) {
  if (brick !== ":forEach" && brick !== ":if" && brick !== ":switch") {
    throw new Error(`Unknown storyboard control node: "${brick}"`);
  }
}
async function renderForEach(returnNode, dataSource, bricks, runtimeContext, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack, initialTracker) {
  const output = getEmptyRenderOutput();
  const size = dataSource.length;
  const rendered = await Promise.all(dataSource.map((item, i) => Promise.all(bricks.map(brickConf => renderBrick(returnNode, brickConf, {
    ...runtimeContext,
    forEachItem: item,
    forEachIndex: i,
    forEachSize: size
  }, rendererContext, parentRoutes, menuRequestReturnNode, slotId, tplStack && new Map(tplStack), initialTracker)))));

  // 多层构件并行异步转换，但转换的结果按原顺序串行合并。
  rendered.flat().forEach(item => {
    mergeRenderOutput(output, item);
  });
  return output;
}
export function getDataStores(runtimeContext) {
  return [runtimeContext.ctxStore, ...runtimeContext.tplStateStoreMap.values(), ...runtimeContext.formStateStoreMap.values()];
}
export function postAsyncRender(output, runtimeContext, stores) {
  flushStableLoadBricks();
  return Promise.all([...output.blockingList, ...stores.map(store => store.waitForAll()), ...runtimeContext.pendingPermissionsPreCheck]);
}
export function createScopedRuntimeContext(runtimeContext) {
  const tplStateStoreScope = [];
  const formStateStoreScope = [];
  const scopedRuntimeContext = {
    ...runtimeContext,
    tplStateStoreScope,
    formStateStoreScope
  };
  return [scopedRuntimeContext, tplStateStoreScope, formStateStoreScope];
}
function loadMenu(menuConf, runtimeContext) {
  if (!menuConf) {
    return;
  }

  // istanbul ignore next
  if (menuConf.type === "brick") {
    // eslint-disable-next-line no-console
    console.error("Set menu with brick is dropped in v3:", menuConf);
    throw new Error("Set menu with brick is dropped in v3");
  }

  // istanbul ignore next
  if (menuConf.type === "resolve") {
    // eslint-disable-next-line no-console
    console.warn("Set menu with resolve is not supported in v3 yet:", menuConf);
    return;
  }
  return asyncComputeRealValue(menuConf, runtimeContext);
}
function mergeRenderOutput(output, newOutput) {
  const {
    blockingList,
    node,
    menuRequestNode,
    ...rest
  } = newOutput;
  output.blockingList.push(...blockingList);
  if (node) {
    if (output.node) {
      let last = output.node;
      while (last.sibling) {
        last = last.sibling;
      }
      last.sibling = node;
    } else {
      output.node = node;
    }
  }
  Object.assign(output, rest);
}
function mergeSiblingRenderMenuRequest(output, newOutput) {
  const menuRequestNode = newOutput.menuRequestNode;
  if (menuRequestNode) {
    if (output.menuRequestNode) {
      let last = output.menuRequestNode;
      while (last.sibling) {
        last = last.sibling;
      }
      last.sibling = menuRequestNode;
    } else {
      output.menuRequestNode = menuRequestNode;
    }
  }
}
function appendMenuRequestNode(menuRequestReturnNode, menuRequestNode) {
  if (!menuRequestNode) {
    return;
  }
  if (menuRequestReturnNode.child) {
    let last = menuRequestReturnNode.child;
    while (last.sibling) {
      last = last.sibling;
    }
    last.sibling = menuRequestNode;
  } else {
    menuRequestReturnNode.child = menuRequestNode;
  }
}
function getEmptyRenderOutput() {
  return {
    blockingList: []
  };
}
export function childrenToSlots(children, originalSlots) {
  let newSlots = originalSlots;
  // istanbul ignore next
  if (process.env.NODE_ENV === "development" && children && !Array.isArray(children)) {
    // eslint-disable-next-line no-console
    console.warn("Specified brick children but not array:", `<${typeof children}>`, children);
  }
  if (Array.isArray(children) && !newSlots) {
    newSlots = {};
    for (const {
      slot: sl,
      ...child
    } of children) {
      const slot = sl !== null && sl !== void 0 ? sl : "";
      const type = isRouteConf(child) ? "routes" : "bricks";
      if (hasOwnProperty(newSlots, slot)) {
        const slotConf = newSlots[slot];
        if (slotConf.type !== type) {
          throw new Error(`Slot "${slot}" conflict between bricks and routes.`);
        }
        slotConf[type].push(child);
      } else {
        newSlots[slot] = {
          type: type,
          [type]: [child]
        };
      }
    }
  }
  return newSlots;
}
function catchLoad(promise, type, name, unknownPolicy) {
  return unknownPolicy === "silent" ? promise.catch(e => {
    // eslint-disable-next-line no-console
    console.error(`Load %s "%s" failed:`, type, name, e);
  }) : promise;
}
function isRouteParamsEqual(a, b) {
  if (isEqual(a, b)) {
    return true;
  }
  const omitNumericKeys = (v, k) => {
    return String(Number(k)) === k;
  };
  const c = omitBy(a, omitNumericKeys);
  const d = omitBy(b, omitNumericKeys);
  return isEqual(c, d);
}
function isRouteConf(child) {
  return !!child.path && !child.brick;
}

/**
 * Find the first matched route among siblings.
 *
 * Return the match result only if it matches the target route.
 */
function matchTargetRoute(route, routes, homepage, pathname) {
  for (const sibling of routes) {
    const match = matchRoute(sibling, homepage, pathname);
    if (match) {
      return sibling === route ? match : null;
    }
  }
  return null;
}
//# sourceMappingURL=Renderer.js.map